import React, { useState } from 'react';
import { FileBarChart2Icon, ShoppingCartIcon, ReceiptIcon, FileTextIcon, DollarSignIcon, TrendingUpIcon, StarIcon, ClockIcon, FilterIcon, SearchIcon, BookmarkIcon, PlusIcon, FileIcon, ArrowRightIcon, DownloadIcon, CalendarIcon, BuildingIcon, UsersIcon, BarChartIcon, HardHatIcon, HomeIcon } from 'lucide-react';
import { ReportFilters } from './ReportFilters';
export const reportCategories = [{
  id: 'ap',
  name: 'Accounts Payable',
  description: 'Invoice, payment, and vendor reports',
  icon: ReceiptIcon,
  reports: [{
    id: 'invoice-aging',
    name: 'Invoice Aging Report',
    description: 'Analysis of invoices by due date and aging buckets'
  }, {
    id: 'invoice-status',
    name: 'Invoice Status Breakdown',
    description: 'Pending approval, paid, rejected, exception queue'
  }, {
    id: 'payment-forecast',
    name: 'Payment Forecast',
    description: 'Upcoming payments and cash flow projections'
  }, {
    id: 'vendor-spend',
    name: 'Vendor Spend Analysis',
    description: 'Spending by vendor, category, and time period'
  }]
}, {
  id: 'requisition',
  name: 'Requisition Reports',
  icon: FileTextIcon,
  description: 'Reports on requisition status, approvals, and trends',
  reports: [{
    id: 'req-summary',
    name: 'Requisition Summary',
    description: 'Status and aging by Entity, user, or department',
    path: 'reports-requisition',
    popular: true,
    newReport: false
  }, {
    id: 'rejected-reqs',
    name: 'Rejected Requisitions',
    description: 'Reasons, categories, requesters',
    path: 'reports-requisition',
    popular: false,
    newReport: false
  }, {
    id: 'req-to-po',
    name: 'Requisition-to-PO Conversion',
    description: 'Metrics on turnaround and conversion rate',
    path: 'reports-requisition',
    popular: false,
    newReport: false
  }]
}, {
  id: 'construction',
  name: 'Construction Industry',
  icon: HomeIcon,
  description: 'Reports for construction industry compliance and payments',
  reports: [{
    id: 'construction-payment',
    name: 'Payment Compliance Report',
    description: 'NSW Security of Payment Act compliance tracking',
    path: 'reports-construction',
    popular: true,
    newReport: true
  }, {
    id: 'construction-projects',
    name: 'Project Financial Status',
    description: 'Financial status by project, contractor, and milestone',
    path: 'reports-construction',
    popular: false,
    newReport: false
  }, {
    id: 'construction-retention',
    name: 'Retention Tracking',
    description: 'Track retention amounts, release dates, and compliance',
    path: 'reports-construction',
    popular: false,
    newReport: false
  }]
}, {
  id: 'financial',
  name: 'Financial & Compliance Reports',
  icon: DollarSignIcon,
  description: 'Reports on budget, compliance, and financial metrics',
  reports: [{
    id: 'budget-vs-actual',
    name: 'Budget vs Actual Spend',
    description: 'Compare by GL code, Entity, Vendor',
    path: 'reports-financial',
    popular: true,
    newReport: false
  }, {
    id: 'tolerance-exceptions',
    name: 'Tolerance Exceptions',
    description: 'Where price or quantity exceeded configured tolerance',
    path: 'reports-financial',
    popular: false,
    newReport: false
  }, {
    id: 'tax-code-usage',
    name: 'Tax Code Usage',
    description: 'Amounts and frequency by country or code',
    path: 'reports-financial',
    popular: false,
    newReport: false
  }, {
    id: 'currency-exchange',
    name: 'Currency Exchange Utilisation',
    description: 'Track conversions and applied exchange rates',
    path: 'reports-financial',
    popular: false,
    newReport: true
  }]
}, {
  id: 'operational',
  name: 'Operational Analytics',
  description: 'Productivity, efficiency, and performance metrics',
  icon: BarChartIcon,
  reports: [{
    id: 'operational-analytics',
    name: 'User Productivity Report',
    description: 'Analysis of user productivity, process efficiency, and system performance'
  }, {
    id: 'process-efficiency',
    name: 'Process Efficiency Analysis',
    description: 'Workflow bottlenecks and optimization opportunities'
  }, {
    id: 'system-usage',
    name: 'System Usage Metrics',
    description: 'Feature adoption and user engagement statistics'
  }]
}];
export function ReportsOverview({
  setCurrentView,
  setSelectedReport,
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFavorites, setShowFavorites] = useState(false);
  // Mock favorites - in a real application, this would be stored in a database or local storage
  const [favorites, setFavorites] = useState(['po-summary', 'invoice-aging', 'budget-vs-actual']);
  const toggleFavorite = reportId => {
    if (favorites.includes(reportId)) {
      setFavorites(favorites.filter(id => id !== reportId));
    } else {
      setFavorites([...favorites, reportId]);
    }
  };
  const handleSelectReport = (report, categoryId) => {
    console.log(`Selecting report: ${report.id} from category: ${categoryId}`);
    // Create a completely new report object
    const reportData = {
      ...report,
      categoryId,
      _timestamp: Date.now()
    };
    // First clear any existing report
    setSelectedReport(null);
    // Then set the current view
    const targetView = report.path || `reports-${categoryId}`;
    setCurrentView(targetView);
    // After a brief delay, set the selected report to ensure components fully unmount/remount
    setTimeout(() => {
      console.log(`Setting selected report with delay: ${reportData.id}`);
      setSelectedReport(reportData);
    }, 100);
  };
  // Filter reports based on search term, category, and favorites
  const filteredCategories = reportCategories.map(category => {
    const filteredReports = category.reports.filter(report => {
      const matchesSearch = searchTerm === '' || report.name.toLowerCase().includes(searchTerm.toLowerCase()) || report.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || selectedCategory === category.id;
      const matchesFavorites = !showFavorites || favorites.includes(report.id);
      return matchesSearch && matchesCategory && matchesFavorites;
    });
    return {
      ...category,
      reports: filteredReports
    };
  }).filter(category => category.reports.length > 0);
  // Get popular reports across all categories
  const popularReports = reportCategories.flatMap(category => category.reports.filter(report => report.popular).map(report => ({
    ...report,
    categoryId: category.id,
    categoryName: category.name
  }))).slice(0, 4);
  // Get new reports across all categories
  const newReports = reportCategories.flatMap(category => category.reports.filter(report => report.newReport).map(report => ({
    ...report,
    categoryId: category.id,
    categoryName: category.name
  }))).slice(0, 4);
  return <div className="flex flex-col space-y-8" data-id="element-16202">
      <div className="flex justify-between items-start" data-id="element-16203">
        <div data-id="element-16204">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-16205">Reports</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-16206">
            Access analytical, operational, and compliance-focused reports
            across the P2P lifecycle
          </p>
        </div>
      </div>
      {/* Search and filters */}
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-16207">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-16208">
          <div className="relative w-full sm:w-auto sm:flex-1 max-w-md" data-id="element-16209">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-16210">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-16211" />
            </div>
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search reports..." data-id="element-16212" />
          </div>
          <div className="flex space-x-2 w-full sm:w-auto" data-id="element-16213">
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-16214">
              <option value="all" data-id="element-16215">All Categories</option>
              {reportCategories.map(category => <option key={category.id} value={category.id} data-id="element-16216">
                  {category.name}
                </option>)}
            </select>
            <button onClick={() => setShowFavorites(!showFavorites)} className={`inline-flex items-center px-4 py-2 border ${showFavorites ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 bg-white text-gray-700'} text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-16217">
              <StarIcon className={`h-4 w-4 mr-2 ${showFavorites ? 'text-indigo-500' : 'text-gray-400'}`} data-id="element-16218" />
              Favorites
            </button>
          </div>
        </div>
      </div>
      {/* Popular and New Reports */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-16219">
        {/* Popular Reports */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden" data-id="element-16220">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-16221">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-16222">
              <StarIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-16223" />
              Popular Reports
            </h3>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-16224">
            {popularReports.map(report => <li key={report.id} className="px-4 py-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => handleSelectReport(report, report.categoryId)} data-id="element-16225">
                <div data-id="element-16226">
                  <p className="text-sm font-medium text-indigo-600" data-id="element-16227">
                    {report.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-16228">{report.categoryName}</p>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-16229" />
              </li>)}
            {popularReports.length === 0 && <li className="px-4 py-4 text-sm text-gray-500 text-center" data-id="element-16230">
                No popular reports available
              </li>}
          </ul>
        </div>
        {/* New Reports */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden" data-id="element-16231">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-16232">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-16233">
              <ClockIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-16234" />
              New Reports
            </h3>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-16235">
            {newReports.map(report => <li key={report.id} className="px-4 py-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => handleSelectReport(report, report.categoryId)} data-id="element-16236">
                <div data-id="element-16237">
                  <div className="flex items-center" data-id="element-16238">
                    <p className="text-sm font-medium text-indigo-600" data-id="element-16239">
                      {report.name}
                    </p>
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-16240">
                      New
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-16241">{report.categoryName}</p>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-16242" />
              </li>)}
            {newReports.length === 0 && <li className="px-4 py-4 text-sm text-gray-500 text-center" data-id="element-16243">
                No new reports available
              </li>}
          </ul>
        </div>
      </div>
      {/* All Report Categories */}
      <div className="space-y-6" data-id="element-16244">
        {filteredCategories.map(category => <div key={category.id} className="bg-white shadow-sm rounded-lg overflow-hidden" data-id="element-16245">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex items-center" data-id="element-16246">
              <category.icon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-16247" />
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16248">
                {category.name}
              </h3>
            </div>
            <ul className="divide-y divide-gray-200" data-id="element-16249">
              {category.reports.map(report => <li key={report.id} className="px-4 py-4 hover:bg-gray-50 cursor-pointer" data-id="element-16250">
                  <div className="flex justify-between items-start" data-id="element-16251">
                    <div className="flex-1" onClick={() => handleSelectReport(report, category.id)} data-id="element-16252">
                      <div className="flex items-center" data-id="element-16253">
                        <p className="text-sm font-medium text-indigo-600" data-id="element-16254">
                          {report.name}
                        </p>
                        {report.newReport && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-16255">
                            New
                          </span>}
                      </div>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-16256">
                        {report.description}
                      </p>
                    </div>
                    <div className="ml-4 flex-shrink-0 flex items-center" data-id="element-16257">
                      <button onClick={() => toggleFavorite(report.id)} className="mr-3 text-gray-400 hover:text-yellow-400 focus:outline-none" data-id="element-16258">
                        <StarIcon className={`h-5 w-5 ${favorites.includes(report.id) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} data-id="element-16259" />
                      </button>
                      <button onClick={() => handleSelectReport(report, category.id)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16260">
                        View Report
                      </button>
                    </div>
                  </div>
                </li>)}
            </ul>
          </div>)}
        {filteredCategories.length === 0 && <div className="bg-white shadow-sm rounded-lg p-8 text-center" data-id="element-16261">
            <FileIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-16262" />
            <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-16263">
              No reports found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-16264">
              Try adjusting your search or filter criteria.
            </p>
            <div className="mt-6" data-id="element-16265">
              <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => {
            setSearchTerm('');
            setSelectedCategory('all');
            setShowFavorites(false);
          }} data-id="element-16266">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-16267" />
                Clear Filters
              </button>
            </div>
          </div>}
      </div>
    </div>;
}